import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { KeyValue } from '@angular/common';
import { Observable } from 'rxjs';
import { UploadInput, UploadOutput } from 'ngx-uploader-esb';
import { ToastrService } from 'ngx-toastr';

import { AppConstants } from '../../../../app.constants';
import { PlaylistItemDTO } from '../../../../core/models/view-models/playlist-item-models/playlist-item-dto.model';
import { Launcher } from '../../../../core/models/entities/launcher.type';
import { PlaylistItemType } from '../../../../core/models/enums/playlist-item-type.enum';
import { MawChromeLauncherViewMode } from '../../../../core/enums/mcl-view-mode.enum';
import { CustomFileModel } from '../../../../core/models/entities/file.type';
import { FilesService } from '../../../../core/services/http-services/files.service';
import { FileUploadingEnum } from '../../../../core/enums/file-uploading.enum';
import { ToastrMessageEnum } from '../../../../core/enums/toastr-messages/toastr-message.enum';
import { PlaylistDTO } from '../../../../core/models/view-models/playlist-models/playlist-dto.model';
import { RssTickerConfigs } from '../../../../core/models/entities/rss-ticker.type';
import { PlaylistItemChangeSourceModel } from '../../../../core/models/view-models/playlist-item-models/playlist-item-change-source-model';

@Component({
  selector: 'file',
  templateUrl: './file.component.html',
  styleUrls: [
    '../tab-file.component.scss',
    '../../../modal-playlist-item/playlist-item-dialog.component.scss'
  ]
})
export class FileComponent implements OnChanges, OnInit {
  @Input() playlist: PlaylistDTO;
  @Input() playlistItem: PlaylistItemDTO;
  @Input() playlistItems: PlaylistItemDTO[];
  @Input() itemIndex: number;
  @Input() launchers: Launcher[];
  @Input() savedSelectedLauncher: Launcher = null;
  @Input() uploadInput: UploadInput;
  @Input() recentFiles$: Observable<CustomFileModel[]>;
  @Input() hasRssForegroundItem: boolean;
  @Output() changeLauncher: EventEmitter<PlaylistItemChangeSourceModel> = new EventEmitter();
  @Output() setSelectedLauncherForAllItems: EventEmitter<Launcher> = new EventEmitter();
  @Output() uploadOutput: EventEmitter<UploadOutput> = new EventEmitter();
  @Output() removeItem: EventEmitter<void> = new EventEmitter();
  @Output() changeMCLMode: EventEmitter<PlaylistItemChangeSourceModel> = new EventEmitter();
  @Output() determineDuration: EventEmitter<PlaylistItemDTO> = new EventEmitter();
  @Output() changeSourceData: EventEmitter<PlaylistItemChangeSourceModel> = new EventEmitter();
  @Output() changeSourceField: EventEmitter<PlaylistItemChangeSourceModel> = new EventEmitter();
  @Output() initDurations: EventEmitter<PlaylistItemDTO> = new EventEmitter();
  @Output() updateRecentFiles: EventEmitter<Observable<CustomFileModel[]>> = new EventEmitter();
  @Output() zoomInputChanged: EventEmitter<PlaylistItemChangeSourceModel> = new EventEmitter();
  @Output() stretchContentChanged: EventEmitter<PlaylistItemDTO> = new EventEmitter();

  public selectedLauncher: Launcher = null;
  public mawChromeModes = MawChromeLauncherViewMode;
  public playlistItemName: string;
  public iconsPath = AppConstants.LAUNCHERS_ICONS_PATH;
  public rssInputPlaceholder = AppConstants.RSS_INPUT_PLACEHOLDER;
  public showApplyDurationOption: boolean;
  public showSetLauncherOption: boolean;
  public appConst = AppConstants;

  constructor(
    private readonly filesService: FilesService,
    private readonly toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.playlistItem.PlaylistItemType !== PlaylistItemType.RecentFile) {
      this.playlistItem.LauncherId = null;
      this.playlistItem.launcherFeatures = [];
      this.playlistItem.RssConfigs = null;
      this.playlistItem.PlaylistItemLinks.splice(1);
      this.selectedLauncher = null;

      if (this.playlistItem.PlaylistItemLinks[0]?.FileId) {
        this.determineDuration.emit(this.playlistItem);
      }
    }

    this.recentFiles$ = this.filesService.getRecentAndPlaylistItemFiles(
      this.playlistItem.PlaylistItemLinks.map(source => source.File));

    this.initItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.savedSelectedLauncher || changes.playlistItem) {
      this.selectedLauncher = this.playlistItem.LauncherId ? this.savedSelectedLauncher : null;
    }

    if (!this.playlistItem.LauncherId) {
      this.selectedLauncher = null;
    }
  }

  onChangeMode(selectedMode: string): void {
    const source = new PlaylistItemChangeSourceModel();
    source.item = this.playlistItem;
    source.mclMode = selectedMode;

    this.changeMCLMode.emit(source);
  }

  onLauncherChanged(): void {
    this.playlistItem.LauncherId = this.selectedLauncher.Id;
    this.showSetLauncherOption = !!!this.itemIndex;

    const source = new PlaylistItemChangeSourceModel();
    source.item = this.playlistItem;
    source.launcher = this.selectedLauncher;

    this.changeLauncher.emit(source);
  }

  onExactDurationChange(isChecked: boolean): void {
    this.playlistItem.Duration = isChecked
      ? this.playlistItem.ExactVideoDuration
      : AppConstants.DEFAULT_PLAYLIST_ITEM_DURATION;
  }

  onUploadFile(output: UploadOutput, index: number): void {
    const item = this.playlistItem.PlaylistItemLinks[index];

    switch (output.type) {
      case FileUploadingEnum.Uploading:
        item.percentage = 0;
        item.percentage = output.file.progress.data.percentage;
        break;

      case FileUploadingEnum.Done:
        if (output.file.responseStatus === AppConstants.SUCCSESS_REQUEST_RESPONSE_STATUS) {
          const uploadedFile: CustomFileModel = output.file.response;

          this.updateRecentFiles.emit(this.filesService.addFileToTheRecentFilesList(this.recentFiles$, uploadedFile));
          item.FileId = uploadedFile.Id;

          const source = new PlaylistItemChangeSourceModel();
          source.item = this.playlistItem;
          source.sourceId = index;
          this.changeSourceData.emit(source);
        }
        else {
          this.toastrService.error(output.file.response.Message, `${ToastrMessageEnum.FailedToUploadTitle}: ${output.file.name}`);
          item.percentage = 0;
          item.FileId = null;
        }
        break;
    }

    this.uploadOutput.emit(output);
  }

  onSourceChanged(index: number, e: Event): void {
    const source = new PlaylistItemChangeSourceModel();
    source.item = this.playlistItem;
    source.sourceId = index;
    source.event = e;

    this.updateRecentFiles.emit(this.recentFiles$);
    this.changeSourceData.emit(source);
  }

  onChangeField(index: number): void {
    const source = new PlaylistItemChangeSourceModel();
    source.item = this.playlistItem;
    source.sourceId = index;

    this.changeSourceField.emit(source);
  }

  onDurationChanged(item: PlaylistItemDTO) {
    this.showApplyDurationOption = !!!this.itemIndex;
    item.useExactDuration = false;
  }

  setDurationForAllItems(item: PlaylistItemDTO): void {
    this.playlistItems.forEach(playlistItem => {
      if (item !== playlistItem) {
        playlistItem.Duration = this.playlistItem.Duration;
        playlistItem.useExactDuration = false;
      }
    });

    this.showApplyDurationOption = false;
  }

  onZoomInputChanged(event: Event, index: number) {
    const source = new PlaylistItemChangeSourceModel();
    source.item = this.playlistItem;
    source.sourceId = index;
    source.event = event;

    this.zoomInputChanged.emit(source);
  }

  public valueAscOrder(a: KeyValue<number,string>, b: KeyValue<number,string>): number {
    return a.value.localeCompare(b.value);
  }

  private initItems() {
    this.initDurations.emit(this.playlistItem);
    this.playlistItem.RssConfigs = this.playlistItem.RssConfigs || new RssTickerConfigs();

    if (this.playlistItem.PlaylistItemLinks?.length > 1) {
      this.playlistItemName = this.playlistItem.PlaylistItemTitle;
    }

    if (!this.playlistItem.Launcher || !this.playlistItem.LauncherId) {
      this.playlistItem.LauncherId = null;
      this.selectedLauncher = null;
    }
  }
}
